import React, { Component, Fragment } from 'react'
import { displayDollars } from '../../helpers/displayFormatHelper'
import { displayMMDDYYYY, displayHHMMSS } from '../../helpers/timeHelper'
import startCase from 'lodash/startCase'
import { castArray, uniq } from 'lodash'

import {
  BureauAddress,
  BureauTradeline,
  Code,
  Loan,
  ProfileSummary,
} from './types'

export type Callback<T = void> = (...args: any) => T
export interface Match<T> {
  params: T
}

export interface ParamsID {
  id: string
}

export interface Props {
  loan: Loan
  match: Match<ParamsID>
  fetchLoan: Callback
}

export class UnderwritingCreditReport extends Component<Props> {
  renderAddress = (address: BureauAddress, index: number) => {
    const {
      city,
      firstReportedDate,
      lastUpdatedDate,
      state,
      streetName,
      streetPrefix,
      streetSuffix,
      zip,
    } = address

    const content = (
      <div className="address" key={`address-${index}`}>
        {streetPrefix} {streetName} {streetSuffix}
        <br />
        {city}, {state} {zip}
        <br />
        RPTD: {displayMMDDYYYY(firstReportedDate)} to{' '}
        {displayMMDDYYYY(lastUpdatedDate)}
        <br />
      </div>
    )

    return content
  }

  renderProfileSummarySide = (attributes: object) => {
    if (!attributes) return null

    const renderValue = (value: object | string, key: string) => {
      if (typeof value === 'object') {
        return JSON.stringify(value)
      } else {
        if (key.match(/(Amount|Balance|Payment)$/)) return displayDollars(value)
        if (key.match(/(Date)$/)) return displayMMDDYYYY(value)
        if (value.match(/^\d+$/)) return parseInt(value, 10)
        return value
      }
    }

    const rows = Object.keys(attributes).map((key) => {
      const label = startCase(key)
        .replace(/([a-z])([A-Z0-9])/g, '$1 $2')
        .replace(/([A-Z])([a-z])/g, ' $1$2')
        .replace(/\ +/g, ' ')
      const value = attributes[key]
      const rendered = renderValue(value, key)

      return (
        <tr className="credit-report__dash" key={key}>
          <td>
            <span className="text">{label}</span>
          </td>
          <td className="right profile-summary-values">
            <span className="text">{rendered}</span>
          </td>
        </tr>
      )
    })

    return (
      <table className="table">
        <tbody>{rows}</tbody>
      </table>
    )
  }

  renderProfileSummary = (profileSummary: ProfileSummary) => {
    const keys = Object.keys(profileSummary)
    const midpoint = Math.ceil(keys.length / 2)

    const left = this.renderProfileSummarySide(
      this.pick(profileSummary, keys.slice(0, midpoint))
    )
    const right = this.renderProfileSummarySide(
      this.pick(profileSummary, keys.slice(midpoint, keys.length))
    )

    return (
      <Fragment>
        <div className="credit-report__headline credit-report__dash">
          <span className="text">PROFILE SUMMARY</span>
        </div>
        <table className="table credit-report__table table-two-column">
          <tbody>
            <tr>
              <td>{left}</td>
              <td>{right}</td>
            </tr>
          </tbody>
        </table>
      </Fragment>
    )
  }

  pick(obj: object, keys: string[]) {
    return Object.keys(obj)
      .filter((i) => keys.includes(i))
      .reduce((acc, key) => {
        acc[key] = obj[key]
        return acc
      }, {})
  }

  renderSpecialComment = (specialComment: Code) => {
    if (specialComment.code.trim() === '') return null

    const specialCodes = {
      '09': 'Credit line closed by consumer',
      '10': 'Special handling — Contact subscriber if more information is needed',
      '11': 'Account payments managed by financial counseling program',
      '12': 'Adjustment pending — reported by subscriber',
      '13': 'Account previously in dispute — now resolved — reported by credit grantor',
      '14': 'Account in dispute under Fair Credit Billing Act',
      '18': "Account closed at credit grantor's request",
      '19': "Account closed at consumer's request",
      '20': 'Completed investigation of FCRA dispute — consumer disagrees',
      '22': 'Election of remedy — reported by subscriber',
      '25': 'Paid by cosigner',
      '28': 'Account closed due to transfer or refinance',
      '29': 'Payment deferred',
      '30': 'Assignor — loan has been assumed by another party',
      '31': 'Transferred to another lender',
      '34': 'Debt being paid through insurance',
      '35': 'Paying under a partial or modified payment agreement',
      '38': 'Single-payment loan',
      '39': 'Simple-interest loan',
      '40': 'Purchased by another lender',
      '41': 'Recalled to military active duty',
      '42': 'Payroll deduction',
      '44': 'Student loan permanently assigned to government',
      '45': 'Account payments assured by wage garnishment',
      '46': 'Account acquired by RTC/FDIC/NCUA',
      '47': 'Voluntarily surrendered — then redeemed',
      '48': 'Credit line suspended',
      '49': 'Contingent liability',
      '50': 'Account closed due to refinance',
      '51': 'Account closed due to transfer Code Description',
      '52': 'Account legally paid in full for less than the full balance',
      '53': 'First payment never received',
      '54': 'Affected by natural or declared disaster',
      '55': 'Account paid by collateral',
      '56': 'Now paying',
      '57': 'Redeemed repossession',
      '58': 'Transferred to recovery',
      '59': 'Full termination/Status pending',
      '60': 'Full termination/Obligation satisfied',
      '61': 'Full termination/Balance owing',
      '62': 'Early termination/Status pending',
      '63': 'Early termination/Obligation satisfied',
      '64': 'Early termination/Balance owing',
      '65': 'Early termination/Insurance loss',
      '66': 'Involuntary repossession',
      '67': 'Involuntary repossession/Obligation satisfied',
      '68': 'Involuntary repossession/Balance owing',
      '69': 'Credit card lost or stolen',
      '71': 'Paid by company that originally sold the merchandise',
      '72': 'Foreclosure proceeding started',
      '73': 'Paid through insurance',
      '76': 'Prepaid lease',
      '77': 'Principal deferred/Interest payment only',
      '78': 'Account information disputed by consumer',
      '80': 'Bankruptcy Chapter 11',
      '81': 'Bankruptcy Chapter 12',
      '83': "Account closed at consumer's request and in dispute under FCRA",
      '84': "Account closed at consumer's request and dispute investigation completed — consumer",
      '88': 'Account closed by consumer and in dispute under FCBA',
      '89': 'Acquired from another lender',
      '90': 'Substitute/replacement account',
      '91': 'Guaranteed/insured',
      CH: 'Closed due to inactivity',
    }

    return (
      <tr>
        <td colSpan={6} className="special-comment">
          {specialCodes[specialComment.code] || specialComment.code}
        </td>
      </tr>
    )
  }

  renderTradeline = (tradeline: BureauTradeline, index: number) => {
    const { enhancedPaymentData } = tradeline
    const { specialComment } = enhancedPaymentData

    const specialCommentHTML = this.renderSpecialComment(specialComment)

    return (
      <Fragment key={`tradeline-${index}`}>
        <tr className="tradeline">
          <td className="credit-report-column-1">
            *{tradeline.subscriberDisplayName}
            <br />
            &nbsp;{tradeline.enhancedPaymentData.accountType.code}
            &nbsp;{tradeline.termsDuration.code}
            &nbsp;{tradeline.ecoa.code}
            <br />
            &nbsp;{tradeline.accountNumber}
          </td>
          <td>
            {displayMMDDYYYY(tradeline.openDate)}
            <br />
            {displayMMDDYYYY(tradeline.balanceDate)}
            <br />
            {displayMMDDYYYY(tradeline.lastPaymentDate)}
          </td>
          <td>
            {tradeline.amount[0].value}-{tradeline.amount[0].qualifier.code}
            <br />
            {tradeline.balanceAmount}
            <br />
            {tradeline.monthlyPaymentAmount}
          </td>
          <td>
            {tradeline.amount[1].value}-{tradeline.amount[1].qualifier.code}
            <br />
            {displayMMDDYYYY(
              tradeline.enhancedPaymentData.initialPaymentLevelDate
            )}
            <br />
            {tradeline.amountPastDue}
          </td>
          <td>{tradeline.enhancedPaymentData.accountCondition.code}</td>
          <td>
            {tradeline.enhancedPaymentData.paymentStatus.code}
            <br />
            {tradeline.paymentProfile}
          </td>
        </tr>
        {specialCommentHTML}
      </Fragment>
    )
  }

  renderInquiry = (
    { date, kob, subcode, subscriberDisplayName, terms, type },
    index: number
  ) => {
    return (
      <tr key={`inquiry-${index}`}>
        <td>{subscriberDisplayName}</td>
        <td>{displayMMDDYYYY(date)}</td>
        <td>{subcode}</td>
        <td>{kob.code}</td>
        <td>{terms.code}</td>
        <td>{type.code}</td>
      </tr>
    )
  }

  render() {
    const { loan } = this.props
    if (!loan) return null
    if (Object.keys(loan).length === 0) return null

    const { applicant } = loan
    const { currentCreditReport, user } = applicant
    const { data, rawData } = currentCreditReport

    if (!data) {
      if (rawData) return <pre>{rawData}</pre>

      return (
        <div className="content">
          <span className="text">
            No credit report data found, or data is malformed.
          </span>
        </div>
      )
    }

    const { netConnectResponse } = data
    const { products, referenceId, transactionId } = netConnectResponse
    const { creditProfile } = products
    const {
      addressInformation,
      consumerIdentity,
      header,
      profileSummary,
      riskModel,
      tradeLine,
      inquiry,
    } = creditProfile

    const addresses = addressInformation.map(this.renderAddress)

    const riskModelHTML = riskModel.map((model) => (
      <tr key={model.modelIndicator.code}>
        <td>
          {model.modelIndicator.code} = {model.score} SCORE FACTORS:{' '}
          {model.scoreFactorCodeOne}, {model.scoreFactorCodeTwo},{' '}
          {model.scoreFactorCodeThree}, {model.scoreFactorCodeFour}
          <br />
        </td>
      </tr>
    ))

    const tradelineHTML = tradeLine.map(this.renderTradeline.bind(this))

    const inquiriesHTML = castArray(inquiry).map(this.renderInquiry)

    const dob = uniq(
      castArray(consumerIdentity)
        .map((id) => id.dob && displayMMDDYYYY(id.dob))
        .filter((x) => x)
    ).join('<br/>')

    return (
      <div className="content">
        <div className="credit-report">
          <div className="credit-report__headline credit-report__dash">
            <span className="text">Applicant Credit Report</span>
          </div>
          <table className="table credit-report__table">
            <tbody>
              <tr>
                <td>TRANSACTIONID: {transactionId}</td>
                <td>REF #: {referenceId}</td>
                <td>DATE: {displayMMDDYYYY(header.reportDate)}</td>
                <td>TIME: {displayHHMMSS(header.reportTime)}</td>
              </tr>
            </tbody>
          </table>
          <table className="table credit-report__table">
            <tbody>
              <tr>
                <td>
                  <div>
                    {user.firstName} {user.lastName}
                  </div>
                  {addresses}
                </td>
                <td>
                  <div>
                    SS: {creditProfile.ssn && creditProfile.ssn.number}
                    <br />
                    DOB: {dob}
                    <br />
                    <br />
                  </div>
                </td>
                <td>
                  {/* TODO
                  <div>
                    E: <br/>
                    Zip: <br/>
                    RPTD: N/A to N/A<br/>
                    <br/>
                  </div>
                  */}
                </td>
              </tr>
            </tbody>
          </table>
          <div className="credit-report__headline credit-report__dash">
            <span className="text">Consumer Statement</span>
          </div>
          <div>
            <br />
            <br />
          </div>
          <div className="credit-report__headline credit-report__dash">
            <span className="text">Messages</span>
          </div>
          <div>
            <br />
            <br />
          </div>
          {this.renderProfileSummary(profileSummary)}
          <div className="credit-report__headline credit-report__dash">
            <span className="text">SCORE SUMMARY</span>
          </div>
          <table className="table credit-report__table">
            <tbody>{riskModelHTML}</tbody>
          </table>
          <div className="credit-report__headline credit-report__dash">
            <span className="text">TRADES</span>
          </div>
          <table className="table credit-report__table">
            <thead>
              <tr>
                <th style={{ width: '20%' }}>
                  SUBSCRIBER
                  <br />
                  TYP TRM ECOA
                  <br />
                  ACCOUNT#
                </th>
                <th>
                  OPEN
                  <br />
                  BALDATE
                  <br />
                  LAST PD
                </th>
                <th>
                  AMT-TYP1
                  <br />
                  BALANCE
                  <br />
                  MONTH PAY
                </th>
                <th>
                  AMT-TYP2
                  <br />
                  PYMT LEVEL
                  <br />
                  PAST DUE
                </th>
                <th>ACCTCOND</th>
                <th>
                  PYMT STATUS
                  <br />
                  PYMT HISTORY BY MONTH
                </th>
              </tr>
            </thead>
            <tbody>{tradelineHTML}</tbody>
          </table>
          <div className="credit-report__headline credit-report__dash">
            <span className="text">Inquiries</span>
          </div>
          <table className="table credit-report__table">
            <thead>
              <tr>
                <th>Subscriber</th>
                <th>Date</th>
                <th>Subcode</th>
                <th>KOB</th>
                <th>Terms</th>
                <th>Type</th>
              </tr>
            </thead>
            <tbody>{inquiriesHTML}</tbody>
          </table>
        </div>
      </div>
    )
  }
}
